// web/src/theme/applyTheme/apply.ts —— 对外 API：本机偏好读取 + applyTheme / initTheme / 各档位 setter
// 拆自 web/src/theme/applyTheme.ts 的同名分区（内容原样搬运）；由 web/src/theme/applyTheme.ts 以 export * 聚合，导出面对调用方不变。

import { CHART_COLORS, CHART_PALETTE } from '../../utils/chartTheme';
import { syncPaletteColors } from '../palette';
import { BORDER_LEVELS, DEFAULT_BORDER_LEVEL, getBorderLevel, type BorderLevelDef, type BorderToneSpec } from '../borders';
import {
  DEFAULT_DENSITY,
  DEFAULT_LANDING,
  DEFAULT_MENU_EXPAND,
  DEFAULT_MOTION,
  DEFAULT_RADIUS,
  DEFAULT_TIME_FMT,
  DEFAULT_WIDTH,
  MOTION_MEDIA_QUERY,
  getDensityLevel,
  getLandingLevel,
  getMenuExpandLevel,
  getMotionLevel,
  getRadiusLevel,
  getTimeFmtLevel,
  getWidthLevel,
  resolveRadius,
  type DensityLevelDef,
  type RadiusLevelDef,
  type WidthLevelDef,
} from '../uiPrefs';
import { DEFAULT_THEME_KEY, getTheme, type ThemeDef } from '../themes';

import { BORDER_STORAGE_KEY, DENSITY_STORAGE_KEY, LANDING_STORAGE_KEY, MENU_EXPAND_STORAGE_KEY, MENU_PINNED_STORAGE_KEY, MOTION_STORAGE_KEY, RADIUS_STORAGE_KEY, STORAGE_KEY, TIME_FMT_STORAGE_KEY, WIDTH_STORAGE_KEY, applied } from './_color';
import { emitHostTheme } from './_hostTheme';
import { LEGACY_THEME_KEYS, syncChartTheme, writeVars } from './_storage';
import { borderBodyVars, borderRootVars, densityRootVars, radiusBodyVars, radiusRootVars, resolveMotion, themeBodyVars, themeRootVars, widthRootVars } from './_tokens';

/** 读取本机保存的主题键（下线键按迁移表落地并写回，未保存 / 键已失效时回退默认主题） */
export function readStoredTheme(): string {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    const migrated = stored ? LEGACY_THEME_KEYS[stored] : undefined;
    const key = getTheme(migrated ?? stored).key;
    if (migrated) {
      /* 下线键只纠正一次：写回后存储值与界面一致，后续读取不再走迁移分支 */
      try {
        localStorage.setItem(STORAGE_KEY, key);
      } catch {
        /* 隐私模式等存储不可用时忽略：本次会话仍按迁移结果生效 */
      }
    }
    return key;
  } catch {
    return DEFAULT_THEME_KEY;
  }
}

/** 读取本机保存的线框档位（未保存 / 键已失效时回退默认档） */
export function readStoredBorder(): string {
  try {
    return getBorderLevel(localStorage.getItem(BORDER_STORAGE_KEY)).key;
  } catch {
    return DEFAULT_BORDER_LEVEL;
  }
}

/** 读取本机保存的内容宽度档 / 动效档 / 圆角档（统一回退到各自默认档） */
export function readStoredWidth(): string {
  try {
    return getWidthLevel(localStorage.getItem(WIDTH_STORAGE_KEY)).key;
  } catch {
    return DEFAULT_WIDTH;
  }
}

export function readStoredMotion(): string {
  try {
    return getMotionLevel(localStorage.getItem(MOTION_STORAGE_KEY)).key;
  } catch {
    return DEFAULT_MOTION;
  }
}

export function readStoredRadius(): string {
  try {
    return getRadiusLevel(localStorage.getItem(RADIUS_STORAGE_KEY)).key;
  } catch {
    return DEFAULT_RADIUS;
  }
}

export function readStoredDensity(): string {
  try {
    return getDensityLevel(localStorage.getItem(DENSITY_STORAGE_KEY)).key;
  } catch {
    return DEFAULT_DENSITY;
  }
}

/** 登录落点（`bcc.landing`）：只在"没有 401 回跳目标"时生效，见 pages/login 的 resolveRedirect */
export function readStoredLanding(): string {
  try {
    return getLandingLevel(localStorage.getItem(LANDING_STORAGE_KEY)).key;
  } catch {
    return DEFAULT_LANDING;
  }
}

/** 登录落点的实际路径（供登录页直接使用，避免页面自己再查一次表） */
export function landingPath(): string {
  return getLandingLevel(readStoredLanding()).path;
}

/**
 * 应用界面设置（主题 + 线框 + 圆角 + 宽度 + 动效）：写入变量 + 更新 body 上的
 * `data-bcc-theme` / `data-bcc-border` / `data-bcc-motion`（供样式钩子与验收定位）+ 持久化 + 同步图表色。
 * persist=false 用于首屏初始化（值已来自存储，不必回写）。
 *
 * **行内覆盖的边界**：默认主题 + 全部默认偏好 == tokens.css 现值，此时不写任何行内值；
 * 只要有一项偏离默认，就**只写偏离的那一组**（主题组 / 线框组 / 圆角组 / 宽度组），
 * 避免动一个开关就把其它 token 固化成行内值、让 tokens.css 失去事实源地位。
 */
export function applyTheme(key: string, persist = true): void {
  const theme = getTheme(key);
  const border = getBorderLevel(readStoredBorder());
  const radius = getRadiusLevel(readStoredRadius());
  const width = getWidthLevel(readStoredWidth());
  const density = getDensityLevel(readStoredDensity());
  const motionKey = readStoredMotion();

  const themeIsDefault = theme.key === DEFAULT_THEME_KEY;
  const borderIsDefault = border.key === DEFAULT_BORDER_LEVEL;
  const radiusIsDefault = radius.key === DEFAULT_RADIUS;
  const widthIsDefault = width.key === DEFAULT_WIDTH;
  const densityIsDefault = density.key === DEFAULT_DENSITY;

  const rootPayload: Record<string, string> = {
    ...(themeIsDefault ? {} : themeRootVars(theme)),
    ...(borderIsDefault ? {} : borderRootVars(theme, border)),
    ...(radiusIsDefault ? {} : radiusRootVars(theme, radius)),
    ...(widthIsDefault ? {} : widthRootVars(width)),
    ...(densityIsDefault ? {} : densityRootVars(density)),
  };
  const bodyPayload: Record<string, string> = {
    ...(themeIsDefault ? {} : themeBodyVars(theme)),
    ...(borderIsDefault ? {} : borderBodyVars(theme, border)),
    ...(radiusIsDefault ? {} : radiusBodyVars(theme, radius)),
  };
  writeVars(document.documentElement, rootPayload, applied.root);
  writeVars(document.body, bodyPayload, applied.body);
  document.body.dataset.bccTheme = theme.key;
  document.body.dataset.bccBorder = border.key;
  document.body.dataset.bccMotion = resolveMotion(motionKey);
  document.body.dataset.bccDensity = density.key;
  syncChartTheme(theme);
  bindSystemMotionListener();
  if (persist) {
    try {
      localStorage.setItem(STORAGE_KEY, theme.key);
    } catch {
      /* 隐私模式等存储不可用时忽略：主题仅在本次会话生效 */
    }
  }
  /* 同源嵌入页（WeKnora 的知识菜单与「智能辅助」卡）靠这份快照跟随主题。
   * 位置必须在 writeVars 之后：emitHostTheme 读的是已经生效的计算样式，不重算派生逻辑。 */
  emitHostTheme();
}

/** 「跟随系统」时的系统设置监听：只绑一次，系统改"减少动态效果"后立即重新下发 */
let systemMotionBound = false;
function bindSystemMotionListener(): void {
  if (systemMotionBound || typeof window.matchMedia !== 'function') return;
  systemMotionBound = true;
  try {
    window.matchMedia(MOTION_MEDIA_QUERY).addEventListener('change', () => {
      if (readStoredMotion() === 'system') applyTheme(readStoredTheme(), false);
    });
  } catch {
    /* 老浏览器无 addEventListener：忽略，仅首屏按当时设置解析一次 */
  }
}

/** 持久化某一项界面偏好（不触发下发，由调用方统一 applyTheme，避免多次重排） */
function persistPref(storageKey: string, value: string): void {
  try {
    localStorage.setItem(storageKey, value);
  } catch {
    /* 存储不可用时本次会话内仍生效 */
  }
}

/** 切换线框档位：持久化后立即重下发 */
export function setBorderLevel(level: string): void {
  persistPref(BORDER_STORAGE_KEY, getBorderLevel(level).key);
  applyTheme(readStoredTheme(), false);
}

export function setWidthLevel(level: string): void {
  persistPref(WIDTH_STORAGE_KEY, getWidthLevel(level).key);
  applyTheme(readStoredTheme(), false);
}

export function setMotionLevel(level: string): void {
  persistPref(MOTION_STORAGE_KEY, getMotionLevel(level).key);
  applyTheme(readStoredTheme(), false);
}

export function setRadiusLevel(level: string): void {
  persistPref(RADIUS_STORAGE_KEY, getRadiusLevel(level).key);
  applyTheme(readStoredTheme(), false);
}

export function setDensityLevel(level: string): void {
  persistPref(DENSITY_STORAGE_KEY, getDensityLevel(level).key);
  applyTheme(readStoredTheme(), false);
}

/** 登录落点不参与视觉下发（不影响任何 token），故只持久化、不触发 applyTheme */
export function setLandingLevel(level: string): void {
  persistPref(LANDING_STORAGE_KEY, getLandingLevel(level).key);
}

/** 侧栏分组默认展开档（current / pinned / all） */
export function readStoredMenuExpand(): string {
  try {
    return getMenuExpandLevel(localStorage.getItem(MENU_EXPAND_STORAGE_KEY)).key;
  } catch {
    return DEFAULT_MENU_EXPAND;
  }
}

export function setMenuExpandLevel(level: string): void {
  persistPref(MENU_EXPAND_STORAGE_KEY, getMenuExpandLevel(level).key);
}

/**
 * 「常开分组」勾选的域键列表（仅供 `pinned` 档使用）。
 * 与 `bcc.menu.openKeys` 分两个键：后者记的是"用户手动收展过什么"，
 * 混在一起会让"逛过的分组"被误当偏好（见 layouts/RegistrySidebar 的持久化口径）。
 */
export function readStoredMenuPinned(): string[] {
  try {
    const raw = localStorage.getItem(MENU_PINNED_STORAGE_KEY);
    if (!raw) return [];
    const v = JSON.parse(raw);
    return Array.isArray(v) ? v.filter((k) => typeof k === 'string') : [];
  } catch {
    return [];
  }
}

export function setMenuPinned(keys: string[]): void {
  try {
    localStorage.setItem(MENU_PINNED_STORAGE_KEY, JSON.stringify(keys));
  } catch {
    /* 存储不可用时本次会话内仍生效 */
  }
}

/** 时间显示档（absolute / relative）：只影响展示，导出与接口恒为绝对值 */
export function readStoredTimeFmt(): string {
  try {
    return getTimeFmtLevel(localStorage.getItem(TIME_FMT_STORAGE_KEY)).key;
  } catch {
    return DEFAULT_TIME_FMT;
  }
}

export function setTimeFmtLevel(level: string): void {
  persistPref(TIME_FMT_STORAGE_KEY, getTimeFmtLevel(level).key);
}

/** 首屏初始化：在 React 渲染前调用，避免先渲染默认色再跳变 */
export function initTheme(): void {
  applyTheme(readStoredTheme(), false);
}
